
import { Component, Prop, Watch } from 'vue-property-decorator';
import ModalView from '@/components/helper/modal-view.vue';
import ListBase from '@/lib/listbase';
import { processDailySalesExcelFile } from './utils/objectives-import';
import { applyCustomFilters } from '../../utils/sheet-utils';

interface SheetData {
    headers: string[];
    items: any[];
}

const IMPORT_TYPE_MAP: Record<'internal' | 'external' | 'total', number> = {
    internal: 0,
    external: 1,
    total: 2
};

@Component({
    components: { ModalView }
})
export default class ImportChanges extends ListBase {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ type: Array, default: () => [] }) monthHeaders!: string[];
    @Prop({ default: '' }) readonly marcaSeleccionada!: string;

    @Prop({ required: true }) readonly year!: number;
    @Prop({ required: true }) readonly month!: number;
    @Prop({ type: Array }) dealers!: string[];

    modalShow: boolean = false;
    file: File | null = null;
    importedSheet: { headers: string[]; items: any[] } = { headers: [], items: [] };
    importLoading: boolean = false;
    exportLoading: boolean = false;

    importedCurrentPage: number = 1;
    importedItemsPerPage: number = 5;
    importedItemsPerPageOptions: number[] = [5, 10, 15];

    importTypeSelect: 'total' | 'internal' | 'external' | null = null;

    modalSetting: any = {
        entity: "sale",
        width: "95%",
        showForm: false,
        titleFull: 'Importar Daily Sales',
        icon: 'mdi-upload',
        saveText: 'Actualizar Daily Sales',
        saveIcon: 'mdi-upload',
        disabledSave: true,
        customSave: true,
        persistent: false
    };

    get importTypeItems() {
        return [
            { text: 'Todo', value: 'total' },
            { text: 'Internos', value: 'internal' },
            { text: 'Externos', value: 'external' }
        ];
    }

    get disabled(): boolean {
        return this.file === null;
    }

    get hasImportedSheet(): boolean {
        return this.importedSheet && this.importedSheet.headers.length > 0;
    }

    generateHeaders(headerRow: string[]): any[] {
        return headerRow.map(col => ({
            text: col,
            value: col
        }));
    }

    get paginatedItems(): any[] {
        const start = (this.importedCurrentPage - 1) * this.importedItemsPerPage;
        return this.importedSheet.items.slice(start, start + this.importedItemsPerPage);
    }

    get importedPageStart(): number {
        return this.importedSheet.items.length
            ? (this.importedCurrentPage - 1) * this.importedItemsPerPage + 1
            : 0;
    }

    get importedPageEnd(): number {
        return Math.min(this.importedCurrentPage * this.importedItemsPerPage, this.importedSheet.items.length);
    }

    get importedTotalPages(): number {
        return Math.ceil(this.importedSheet.items.length / this.importedItemsPerPage);
    }

    @Watch('value', { immediate: true })
    onValueChanged(val: boolean) {
        this.modalShow = val;
        if (val) {
            this.initForm();
        }
    }

    @Watch('modalShow')
    onModalShowChanged(val: boolean) {
        if (!val) {
            this.$emit('input', false);
        }
    }

    private updateModalTitle(): void {
        switch (this.importTypeSelect) {
            case 'internal':
                this.modalSetting.titleFull = 'Importar Objetivos - Revoshop';
                break;
            case 'external':
                this.modalSetting.titleFull = 'Importar Objetivos - Dealers terceros';
                break;
            case 'total':
                this.modalSetting.titleFull = 'Importar Objetivos - Revoshop y Dealers terceros';
                break;
            default:
                this.modalSetting.titleFull = 'Importar Objetivos';
        }
    }

    initForm(): void {
        this.updateModalTitle();
        this.modalSetting.saveText = 'Actualizar Daily Sales';
        this.reset();
    }

    @Watch('importTypeSelect')
    onImportTypeSelectChanged(val: 'internal' | 'external' | 'total'): void {
        this.updateModalTitle();
        const idx = IMPORT_TYPE_MAP[val];
        console.log('[ImportChanges] 🔄 importTypeSelect:', val, '→ emito índice', idx);
        this.$emit('update-import-type', idx);
    }

    reset(): void {
        this.file = null;
        this.importedSheet = { headers: [], items: [] };
        this.modalSetting.disabledSave = true;
        this.importedCurrentPage = 1;
        this.importedItemsPerPage = 5;
    }

    async importFile(): Promise<void> {

        if (this.file && !this.importTypeSelect) {
            this.$toast.info("Por favor selecciona dónde importar los datos");
            return;
        }

        if (!this.file) return;
        this.importLoading = true;

        try {
            const view = this.importTypeSelect ? this.importTypeSelect.toLowerCase().trim() : "";

            if (view === "total") {
                const dataRevoshop = await processDailySalesExcelFile(this.file, "Daily Report Revoshop");
                const dataDealers = await processDailySalesExcelFile(this.file, "Daily Report Dealers terceros");

                if (dataRevoshop && dataDealers) {
                    const combinedHeaders = dataRevoshop.headers;
                    const combinedItems = [...dataRevoshop.items, ...dataDealers.items];

                    let combinedSheet: SheetData = { headers: combinedHeaders, items: combinedItems };

                    combinedSheet = this.applyCustomFilters(combinedSheet);

                    this.importedSheet = combinedSheet;
                    this.modalSetting.disabledSave = false;
                } else {
                    this.$toast.error("No se encontraron datos válidos en ambas hojas");
                }
            } else if (view === "internal") {
                const data = await processDailySalesExcelFile(this.file, "Daily Report Revoshop");
                if (data) {
                    const filtered = this.filterImportChanges(data);
                    if (filtered.items.length > 0) {
                        this.importedSheet = filtered;
                        this.modalSetting.disabledSave = false;
                    } else {
                        this.$toast.error("No se encontraron datos numéricos válidos para importar.");
                    }
                } else {
                    this.$toast.error("No se encontraron datos válidos en la hoja 'Daily Report Revoshop'");
                }
            } else if (view === "external") {
                const data = await processDailySalesExcelFile(this.file, "Daily Report Dealers terceros");
                if (data) {
                    const filtered = this.filterImportChanges(data);
                    if (filtered.items.length > 0) {
                        this.importedSheet = filtered;
                        this.modalSetting.disabledSave = false;
                    } else {
                        this.$toast.error("No se encontraron datos numéricos válidos para importar.");
                    }
                } else {
                    this.$toast.error("No se encontraron datos válidos en la hoja 'Daily Report Dealers terceros'");
                }
            } else {
                console.error("Tipo de vista no válido en importFile:", view);
                this.$toast.error("Tipo de vista no válido: " + view);
            }
        } catch (error) {
            console.error("Error al procesar el archivo:", error);
            this.$toast.error("Error al importar el archivo");
        } finally {
            this.importLoading = false;
        }
    }

    applyCustomFilters(sheet: SheetData): SheetData {
        return applyCustomFilters(
            sheet,
            ["DEALER", "ITEMS"],
            this.dealers,
            (msg) => this.$toast.info(msg)
        );
    }

    get importedSheetTitle(): string {
        const view = this.importTypeSelect ? this.importTypeSelect.toLowerCase().trim() : "";
        if (view === 'internal') {
            return 'Datos - Revoshop';
        } else if (view === 'external') {
            return 'Datos - Dealers terceros';
        } else if (view === 'total') {
            return 'Datos - Revoshop y Dealers terceros';
        } else {
            return 'Datos';
        }
    }

    private parseCell(raw: any): number | null {
        const s = raw == null ? '' : String(raw).trim();
        if (s === '' || /^[-–—]$/.test(s)) return null;
        const n = Number(s.replace(',', '.'));
        return isNaN(n) ? null : n;
    }

    private filterImportChanges(sheet: SheetData): SheetData {
        const [dealerCol, itemCol, ...numCols] = sheet.headers;
        const outputItems: any[] = [];
        let currentDealer: Record<string, any> | null = null;
        let bufferRows: any[] = [];
        let validCount = 0;
        const usedCols = new Set<string>();

        const flushGroup = () => {
            if (!currentDealer) return;
            if (validCount > 0) {
                // console.info(`[ImportChanges] Dealer "${currentDealer[dealerCol]}" importado con ${validCount} valores válidos.`);
                outputItems.push({ ...currentDealer });
                bufferRows.forEach(r => outputItems.push(r));
            } else {
                // console.info(`[ImportChanges] Dealer "${currentDealer[dealerCol]}" omitido (0 datos válidos).`);
            }
            bufferRows = [];
            validCount = 0;
        };

        for (const row of sheet.items) {
            const isDealerRow = String(row[dealerCol] ?? '').trim() !== '';
            if (isDealerRow) {
                flushGroup();
                currentDealer = row;
            } else if (currentDealer) {
                const parsed: any = { [dealerCol]: '', [itemCol]: row[itemCol] };
                let rowHasData = false;

                for (const col of numCols) {
                    const v = this.parseCell(row[col]);
                    if (v !== null) {
                        parsed[col] = v;
                        validCount++;
                        rowHasData = true;
                        usedCols.add(col);
                    } else {
                        parsed[col] = null;
                    }
                }

                if (rowHasData) {
                    bufferRows.push(parsed);
                } else {
                    // console.info(`[ImportChanges] Fila ignorada (sin datos) dealer="${currentDealer[dealerCol]}", ítem="${row[itemCol]}"`);
                }
            }
        }
        flushGroup();

        const validNumCols = numCols.filter(col => usedCols.has(col));

        const finalHeaders = [dealerCol, itemCol, ...validNumCols];

        const finalItems = outputItems.map(row => {
            const pruned: any = {};
            for (const h of finalHeaders) {
                pruned[h] = row[h] ?? null;
            }
            return pruned;
        });

        return {
            headers: finalHeaders,
            items: finalItems
        };
    }

    async save(): Promise<any> {
        try {
            const filteredRows = this.importedSheet.items.map(item => {
                const row: any = {};
                if (item.DEALER !== undefined) {
                    row.DEALER = typeof item.DEALER === 'string' ? item.DEALER.toUpperCase() : item.DEALER;
                }

                row.ITEMS = {};
                Object.keys(item).forEach(key => {
                    if (key === 'DEALER') return;

                    const value = item[key];
                    if (value !== undefined && value !== null) {
                        row.ITEMS[key] = value;
                    }
                });
                return row;
            });

            const output = {
                headers: this.importedSheet.headers,
                rows: filteredRows
            };

            this.$emit('update-daily-sales', output);

            this.$toast.success("Daily Sales actualizados correctamente");
            console.log("los datos que se importaron son :", output);
            this.$emit('save-success');
            this.cancel();
            return true;
        } catch (error) {
            console.error("Error al actualizar Daily Sales:", error);
            this.$toast.error("Error actualizando Daily Sales");
            return false;
        }
    }

    cancel(): void {
        this.reset();
        this.modalShow = false;
        this.$emit('input', false);
    }

    async exportToExcel() {
        if (!this.marcaSeleccionada || !this.year || !this.month) {
            this.$toast.error("Faltan parámetros para exportar el formato");
            return;
        }
        this.exportLoading = true;
        try {
            const payload = {
                manufacturer: this.marcaSeleccionada,
                year: this.year,
                month: this.month
            };
            await this.$store.dispatch('dailyModule/exportDailyReport', payload);
            this.$toast.success("Formato exportado exitosamente");
        } catch (error) {
            console.error("Error al exportar el formato:", error);
            this.$toast.error("Error al exportar el formato");
        } finally {
            this.exportLoading = false;
        }
    }
}
